interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPageChange: (page: number) => void;
  showingFrom: number;
  showingTo: number;
  totalItems: number;
  itemLabel?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeConfig = {
  sm: {
    buttonPadding: 'px-2 py-1',
    buttonRadius: 'rounded',
    textSize: 'text-xs',
    labelSize: 'text-xs',
  },
  md: {
    buttonPadding: 'px-4 py-2',
    buttonRadius: 'rounded-sm',
    textSize: 'text-sm',
    labelSize: 'text-xs',
  },
  lg: {
    buttonPadding: 'px-6 py-3',
    buttonRadius: 'rounded-md',
    textSize: 'text-base',
    labelSize: 'text-sm',
  },
};

export default function PaginationControls({
  currentPage,
  totalPages,
  hasPrevious,
  hasNext,
  onPageChange,
  showingFrom,
  showingTo,
  totalItems,
  itemLabel = 'items',
  className = '',
  size = 'md',
}: PaginationControlsProps) {
  const shouldShowButtons = hasPrevious || hasNext;
  const sizing = sizeConfig[size];

  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisibleButtons = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisibleButtons / 2)
    );
    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage + 1 < maxVisibleButtons) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`${sizing.buttonPadding} ${sizing.buttonRadius} font-semibold !text-text-primary-heading ${
            currentPage === i ? 'btn-purple2' : 'paginationBtn'
          }`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  if (totalItems <= 0) return null;

  return (
    <div
      className={`flex items-center justify-between w-full mt-auto h-fit ${className}`}
    >
      <div>
        <p className={sizing.labelSize}>
          Showing {showingFrom}-{showingTo} of {totalItems} {itemLabel}
        </p>
      </div>
      <div
        className="flex pagination"
        style={{ gap: size === 'sm' ? '0.25rem' : '0.5rem' }}
      >
        <div className="flex gap-2">
          {shouldShowButtons && (
            <>
              <button
                className={`text-text-primary-highlight  paginationBtn !rounded-lg ${sizing.buttonPadding} ${sizing.buttonRadius} font-semibold ${
                  !hasPrevious ? 'opacity-20' : ''
                }`}
                onClick={() => onPageChange(currentPage - 1)}
                disabled={!hasPrevious}
              >
                Prev
              </button>
              {renderPaginationButtons()}
              <button
                className={`text-text-primary-highlight  paginationBtn !rounded-lg ${sizing.buttonPadding} ${sizing.buttonRadius} font-semibold ${
                  !hasNext ? 'opacity-20' : ''
                }`}
                onClick={() => onPageChange(currentPage + 1)}
                disabled={!hasNext}
              >
                Next
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
